import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Custom SVG Icons
const ExpandMoreIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const SaveIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
  </svg>
);

const EmailIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
  </svg>
);

const LockIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
  </svg>
);

const VisibilityIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
  </svg>
);

const VisibilityOffIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
  </svg>
);

const API_BASE_URL = import.meta.env.VITE_API_URL;

const Settings = () => {
  // Email settings
  const [emailSettings, setEmailSettings] = useState({
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    senderName: 'IT Support',
    senderEmail: '',
    enableNotifications: true,
  });

  // Password change state
  const [passwordSettings, setPasswordSettings] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    showPassword: false,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  // Load settings from backend
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(`${API_BASE_URL}/admin/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        // Set the settings from the backend response
        setEmailSettings(response.data.data.email);
        setError(null);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      // Validate email settings if notifications are enabled
      if (emailSettings.enableNotifications) {
        const requiredFields = ['smtpHost', 'smtpPort', 'smtpUser', 'smtpPassword', 'senderEmail'];
        const missingFields = requiredFields.filter(field => !emailSettings[field]);
        
        if (missingFields.length > 0) {
          setError(`Missing required email fields: ${missingFields.join(', ')}`);
          return;
        }
        
        if (emailSettings.smtpHost.includes('gmail') && !emailSettings.smtpUser.includes('@')) {
          setError('Gmail username must be a complete email address');
          return;
        }
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(`${API_BASE_URL}/admin/settings/email`, { email: emailSettings }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        setSaveSuccess(true);
        setError(null);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error('Error saving email settings:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save email settings');
    } finally {
      setLoading(false);
    }
  };

  const testEmailSettings = async () => {
    try {
      // Validate required fields before sending
      const requiredFields = ['smtpHost', 'smtpPort', 'smtpUser', 'smtpPassword', 'senderEmail'];
      const missingFields = requiredFields.filter(field => !emailSettings[field]);
      
      if (missingFields.length > 0) {
        setEmailTestResult({
          success: false,
          message: `Missing required fields: ${missingFields.join(', ')}`
        });
        return;
      }
      
      // Gmail validation
      if (emailSettings.smtpHost.includes('gmail') && !emailSettings.smtpUser.includes('@')) {
        setEmailTestResult({
          success: false,
          message: 'Gmail username must be a complete email address'
        });
        return;
      }
      
      setTestingEmail(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(`${API_BASE_URL}/admin/settings/test-email`, emailSettings, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        setEmailTestResult({
          success: true,
          message: 'Test email sent successfully!'
        });
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error('Error testing email settings:', err);
      setEmailTestResult({
        success: false,
        message: err.response?.data?.message || err.message || 'Failed to send test email'
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailSwitchChange = (e) => {
    const { name, checked } = e.target;
    setEmailSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSnackbarClose = () => {
    setSaveSuccess(false);
    setEmailTestResult(null);
  };

  const handlePasswordChange = async () => {
    try {
      if (passwordSettings.newPassword !== passwordSettings.confirmPassword) {
        setError('New passwords do not match');
        return;
      }

      // Enhanced password validation
      const hasUpperCase = /[A-Z]/.test(passwordSettings.newPassword);
      const hasLowerCase = /[a-z]/.test(passwordSettings.newPassword);
      const hasNumbers = /\d/.test(passwordSettings.newPassword);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordSettings.newPassword);
      const isLongEnough = passwordSettings.newPassword.length >= 8;

      if (!isLongEnough) {
        setError('Password must be at least 8 characters long');
        return;
      }
      if (!hasUpperCase) {
        setError('Password must contain at least one uppercase letter');
        return;
      }
      if (!hasLowerCase) {
        setError('Password must contain at least one lowercase letter');
        return;
      }
      if (!hasNumbers) {
        setError('Password must contain at least one number');
        return;
      }
      if (!hasSpecialChar) {
        setError('Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)');
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(`${API_BASE_URL}/admin/change-password`, {
        currentPassword: passwordSettings.currentPassword,
        newPassword: passwordSettings.newPassword,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        setSaveSuccess(true);
        setError(null);
        setPasswordSettings({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          showPassword: false,
        });
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !emailSettings) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="bg-white/95 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold font-poppins bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent mb-6">
          System Settings
        </h1>
      
      {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800 font-poppins">{error}</p>
              </div>
            </div>
          </div>
        )}
      
        <div className="space-y-6">
        {/* Admin Utilities */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold font-poppins text-gray-900 flex items-center gap-2">
                <LockIcon className="w-5 h-5 text-blue-600" />
                Admin Utilities
              </h2>
            </div>
            
            <div className="p-4 sm:p-6">
                {/* Password Change */}
              <div className="bg-blue-50 p-4 sm:p-6 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold font-poppins text-blue-900 mb-4">Change Password</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                      Current Password *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                            type={passwordSettings.showPassword ? 'text' : 'password'}
                            value={passwordSettings.currentPassword}
                            onChange={(e) => setPasswordSettings(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                                    onClick={() => setPasswordSettings(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {passwordSettings.showPassword ? (
                          <VisibilityOffIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <VisibilityIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                      New Password *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                            type={passwordSettings.showPassword ? 'text' : 'password'}
                            value={passwordSettings.newPassword}
                            onChange={(e) => setPasswordSettings(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                        placeholder="Enter new password"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                      Confirm New Password *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                            type={passwordSettings.showPassword ? 'text' : 'password'}
                            value={passwordSettings.confirmPassword}
                            onChange={(e) => setPasswordSettings(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                </div>
                
                <button
                            onClick={handlePasswordChange}
                            disabled={loading}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-poppins disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <SaveIcon className="w-5 h-5" />
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        
        {/* Email Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold font-poppins text-gray-900 flex items-center gap-2">
                <EmailIcon className="w-5 h-5 text-blue-600" />
                Email Settings
              </h2>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                        checked={emailSettings.enableNotifications}
                        onChange={handleEmailSwitchChange}
                        name="enableNotifications"
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm font-medium text-gray-700 font-poppins">
                    Enable Email Notifications
                  </span>
                </div>
                
                {emailSettings.enableNotifications && (
                  <>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-blue-900 mb-3 font-poppins">Email Configuration Requirements:</h4>
                      <div className="text-sm text-blue-800 font-poppins space-y-3">
                        <div>
                          <p className="font-medium mb-2">For Gmail accounts with 2-Factor Authentication (2FA):</p>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                              <li>You MUST use an App Password (16 characters, no spaces)</li>
                              <li>Steps to get an App Password:</li>
                            <ol className="list-decimal list-inside ml-6 space-y-1">
                              <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Google Account Security</a></li>
                                <li>Enable 2-Step Verification if not already enabled</li>
                              <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">App Passwords</a></li>
                                <li>Select "Mail" and your device</li>
                                <li>Copy the generated 16-character password</li>
                                <li>Remove any spaces from the password</li>
                            </ol>
                          </ul>
                        </div>
                        
                        <div>
                          <p className="font-medium mb-2">For Gmail accounts without 2FA:</p>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>Enable "Less secure app access" in your <a href="https://myaccount.google.com/lesssecureapps" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Google Account Settings</a></li>
                              <li>Use your regular Gmail password</li>
                              <li>Note: This is not recommended for security reasons</li>
                          </ul>
                        </div>
                        
                        <div>
                          <p className="font-medium mb-2">Default Gmail Settings:</p>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                              <li>SMTP Host: smtp.gmail.com</li>
                              <li>SMTP Port: 587 (TLS) or 465 (SSL)</li>
                              <li>Username: Your full Gmail address</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                          SMTP Host *
                        </label>
                        <input
                          type="text"
                        name="smtpHost"
                        value={emailSettings.smtpHost}
                        onChange={handleEmailChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                            !emailSettings.smtpHost ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="smtp.gmail.com"
                        />
                        {emailSettings.smtpHost.includes('gmail') && (
                          <p className="mt-1 text-sm text-blue-600 font-poppins">Using Gmail SMTP server</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                          SMTP Port *
                        </label>
                        <input
                          type="number"
                        name="smtpPort"
                        value={emailSettings.smtpPort}
                        onChange={handleEmailChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                            !emailSettings.smtpPort || (emailSettings.smtpHost.includes('gmail') && ![587, 465].includes(Number(emailSettings.smtpPort))) 
                              ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="587"
                        />
                        {emailSettings.smtpHost.includes('gmail') && (
                          <p className="mt-1 text-sm text-blue-600 font-poppins">For Gmail, use 587 (TLS) or 465 (SSL)</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                          SMTP Username *
                        </label>
                        <input
                          type="text"
                        name="smtpUser"
                        value={emailSettings.smtpUser}
                        onChange={handleEmailChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                            !emailSettings.smtpUser || (emailSettings.smtpHost.includes('gmail') && !emailSettings.smtpUser.includes('@'))
                              ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="your-email@gmail.com"
                        />
                        {emailSettings.smtpHost.includes('gmail') && (
                          <p className="mt-1 text-sm text-blue-600 font-poppins">Must be your full Gmail email address</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                          SMTP Password *
                        </label>
                        <input
                          type="password"
                        name="smtpPassword"
                        value={emailSettings.smtpPassword}
                        onChange={handleEmailChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                          emailSettings.smtpHost.includes('gmail') && 
                            (!emailSettings.smtpPassword || 
                            emailSettings.smtpPassword.length !== 16 ||
                             /\s/.test(emailSettings.smtpPassword))
                              ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Enter your app password"
                        />
                        {emailSettings.smtpHost.includes('gmail') && (
                          <div className="mt-1">
                            {emailSettings.smtpPassword ? (
                              emailSettings.smtpPassword.length === 16 && !/\s/.test(emailSettings.smtpPassword) ? (
                                <p className="text-sm text-green-600 font-poppins">✓ Valid App Password format</p>
                              ) : emailSettings.smtpPassword.includes(' ') ? (
                                <p className="text-sm text-red-600 font-poppins">❌ Remove all spaces from the App Password</p>
                              ) : emailSettings.smtpPassword.length !== 16 ? (
                                <p className="text-sm text-red-600 font-poppins">❌ App Password must be exactly 16 characters (current: {emailSettings.smtpPassword.length})</p>
                              ) : (
                                <p className="text-sm text-red-600 font-poppins">❌ Invalid App Password format</p>
                              )
                            ) : (
                              <p className="text-sm text-blue-600 font-poppins">Enter your App Password (16 characters, no spaces)</p>
                            )}
                            
                            {emailSettings.smtpPassword && (
                              <div className="flex items-center gap-4 mt-2">
                                <span className="text-xs font-poppins">Password Format:</span>
                                <span className={`text-xs font-poppins ${emailSettings.smtpPassword.length === 16 ? 'text-green-600' : 'text-red-600'}`}>
                                  {emailSettings.smtpPassword.length === 16 ? '✓' : '❌'} 16 characters
                                </span>
                                <span className={`text-xs font-poppins ${!/\s/.test(emailSettings.smtpPassword) ? 'text-green-600' : 'text-red-600'}`}>
                                  {!/\s/.test(emailSettings.smtpPassword) ? '✓' : '❌'} No spaces
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-yellow-900 mb-3 font-poppins">Security Notice:</h4>
                      <div className="text-sm text-yellow-800 font-poppins">
                        <p className="mb-2">For enhanced security, we strongly recommend:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>Enabling 2-Factor Authentication on your Gmail account</li>
                            <li>Using App Passwords instead of your main account password</li>
                            <li>Creating a dedicated Gmail account for system notifications</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                          Sender Name
                        </label>
                        <input
                          type="text"
                        name="senderName"
                        value={emailSettings.senderName}
                        onChange={handleEmailChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                          placeholder="IT Support"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                          Sender Email *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <EmailIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="email"
                        name="senderEmail"
                        value={emailSettings.senderEmail}
                        onChange={handleEmailChange}
                            className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                              !emailSettings.senderEmail || !emailSettings.senderEmail.includes('@')
                                ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="support@company.com"
                          />
                        </div>
                        {!emailSettings.senderEmail.includes('@') && emailSettings.senderEmail && (
                          <p className="mt-1 text-sm text-red-600 font-poppins">Must be a valid email address</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <button
                        onClick={testEmailSettings}
                        disabled={testingEmail}
                        className="inline-flex items-center gap-2 px-6 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-poppins disabled:opacity-50"
                      >
                        {testingEmail ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            Sending Email...
                          </>
                        ) : (
                          <>
                            <EmailIcon className="w-5 h-5" />
                            Send Test Email
                          </>
                        )}
                      </button>
                      <p className="mt-2 text-sm text-gray-600 font-poppins">
                        All fields marked with * are required before you can send a test email.
                      </p>
                    </div>
                  </>
                )}
              </div>
              
              <div className="border-t border-gray-200 p-4 sm:p-6 flex justify-end">
                <button
                onClick={saveEmailSettings}
                disabled={loading}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-poppins disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <SaveIcon className="w-5 h-5" />
                      Save Email Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Message */}
      {saveSuccess && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <div className="px-6 py-4 bg-green-500 text-white rounded-lg shadow-lg min-w-[300px] max-w-md font-poppins">
            <div className="flex items-center justify-between gap-4">
              <span className="font-medium">Settings saved successfully!</span>
              <button
                onClick={handleSnackbarClose}
                className="text-white hover:text-gray-200 transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Test Results */}
      {emailTestResult !== null && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <div className={`px-6 py-4 rounded-lg shadow-lg min-w-[300px] max-w-md font-poppins ${
            emailTestResult.success ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            <div className="flex items-center justify-between gap-4">
              <span className="font-medium">{emailTestResult.message}</span>
              <button
                onClick={handleSnackbarClose}
                className="text-white hover:text-gray-200 transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 